import { useState } from "react";
import { ApiError } from "@carepass/api-client";
import { useLocation, useNavigate } from "react-router";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import type { AdminSessionEndReason } from "../../app/session-lifecycle";
import { LoginView } from "./components/LoginView";
import type { LoginFeedback, LoginSubmitResult } from "./components/LoginView";
import { useAdminLoginCommand } from "./queries";
import type { Schemas } from "../workspace/api.generated";

const LOGIN_FEEDBACK: Readonly<Record<string, LoginFeedback>> = {
  ADMIN_CREDENTIALS_INVALID: {
    title: "계정 정보를 확인해 주세요",
    message: "관리자 계정 또는 비밀번호가 올바르지 않습니다.",
  },
  ADMIN_TOTP_INVALID: {
    title: "TOTP 코드를 확인해 주세요",
    message: "인증 앱에 표시된 현재 숫자 6자리를 다시 입력해 주세요.",
  },
  ADMIN_RECOVERY_CODE_INVALID: {
    title: "복구코드를 확인해 주세요",
    message: "보관한 복구코드가 올바르지 않거나 이미 사용되었습니다.",
  },
  ADMIN_LOGIN_BLOCKED: {
    title: "로그인 시도가 제한되었습니다",
    message: "잠시 후 관리자 계정과 인증 정보를 다시 확인해 주세요.",
  },
  ADMIN_IP_DENIED: {
    title: "접속 위치를 확인해 주세요",
    message: "허용된 관리자 네트워크에서 다시 접속해 주세요.",
  },
};

export function loginFeedback(error: unknown): LoginFeedback {
  if (error instanceof ApiError) {
    const known = LOGIN_FEEDBACK[error.code];
    if (known) return known;
    if (error.kind === "network") {
      return {
        title: "서버에 연결하지 못했습니다",
        message: "네트워크 연결을 확인한 뒤 다시 시도해 주세요.",
      };
    }
  }
  return {
    title: "로그인하지 못했습니다",
    message: "입력 정보를 확인한 뒤 다시 시도해 주세요.",
  };
}

function loginNotice(
  noticeState: unknown,
  lastEndReason: AdminSessionEndReason | null,
): LoginFeedback | null {
  if (noticeState === "totp-completed" || lastEndReason === "totp-completed") {
    return {
      title: "TOTP 설정을 완료했습니다",
      message: "복구코드를 보관했습니다. 관리자 계정과 현재 TOTP 코드로 다시 로그인해 주세요.",
    };
  }
  if (lastEndReason === "expired") {
    return {
      title: "로그인 시간이 만료되었습니다",
      message: "다시 로그인하면 확인하던 업무 화면으로 돌아갑니다.",
      variant: "warning",
    };
  }
  if (lastEndReason === "unauthorized") {
    return {
      title: "로그인 상태를 다시 확인해 주세요",
      message: "세션을 확인하지 못했습니다. 다시 로그인하면 확인하던 업무 화면으로 돌아갑니다.",
      variant: "warning",
    };
  }
  if (lastEndReason === "totp-confirm-uncertain") {
    return {
      title: "TOTP 설정 결과를 다시 확인해 주세요",
      message: "자동으로 확인 요청을 다시 보내지 않습니다. 관리자 계정으로 다시 로그인해 등록 상태를 확인해 주세요.",
      variant: "warning",
    };
  }
  if (noticeState === "password-change-uncertain" || lastEndReason === "password-change-uncertain") {
    return {
      title: "변경 결과를 다시 확인해 주세요",
      message: "새 비밀번호로 먼저 로그인하고, 실패하면 기존 비밀번호로 다시 시도해 주세요.",
      variant: "warning",
    };
  }
  if (noticeState === "password-changed" || lastEndReason === "password-changed") {
    return {
      title: "비밀번호를 변경했습니다",
      message: "새 비밀번호로 다시 로그인해 다음 보안 단계를 진행해 주세요.",
    };
  }
  return null;
}

export function LoginPage() {
  const login = useAdminLoginCommand();
  const { sessionLifecycle } = useAdminApplication();
  const session = useAdminSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<LoginFeedback | null>(null);
  const noticeState = (location.state as { readonly notice?: unknown } | null)?.notice;
  const notice = loginNotice(noticeState, session.lastEndReason);

  const clearFeedback = () => {
    setError(null);
    sessionLifecycle.clearEndReason();
    if (notice) {
      void navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  };

  const submit = async (input: Schemas["AuthenticateAdminRequest"]): Promise<LoginSubmitResult> => {
    setError(null);
    try {
      await login(input);
      return "authenticated";
    } catch (cause) {
      if (cause instanceof ApiError && cause.kind === "aborted") return "rejected";
      if (cause instanceof ApiError && cause.code === "ADMIN_TOTP_INVALID" && !input.totpCode && !input.recoveryCode) {
        return "second-factor";
      }
      setError(loginFeedback(cause));
      return "rejected";
    }
  };

  return <LoginView error={error} notice={error ? null : notice} onClearError={clearFeedback} onSubmit={submit} />;
}
